import { z } from 'zod'
import { oc } from '@orpc/contract'
import { DECISION_REASONS } from '../../persona/model/decision-reason.schema'

// Patient-facing: the authenticated patient reads their own portal-access status (used by patient-web
// to gate PHI routes and to disambiguate a 403 between "needs 2FA" and "identity not verified").
export const getPortalAccess = oc
  .route({ method: 'GET', path: '/v2/patient/me/portal-access' })
  .output(
    z.object({
      identityVerified: z.boolean(),
    }),
  )

// Provider/admin-facing: mark a patient's identity verified (or undo). The mutation runs via the system
// repo because providers cannot write a patient's ProjectMembership; the auth decorator + an explicit
// patient-caller rejection are the authorization gate.
export const markIdentityVerified = oc
  .route({ method: 'POST', path: '/v2/patient/{id}/identity-verification' })
  .input(z.object({ id: z.string(), verified: z.boolean() }))
  .output(
    z.object({
      id: z.string(),
      identityVerified: z.boolean(),
      identityVerifiedAt: z.string().optional(),
      identityVerifiedBy: z.string().optional(),
    }),
  )

export const identityStatusSchema = z.enum([
  'not_started',
  'pending',
  'review_required',
  'verified_auto',
  'verified_manual',
  'rejected',
])

export const identityMethodSchema = z.enum(['persona', 'manual'])

const matchOutcomeSchema = z.enum(['match', 'mismatch', 'unavailable'])

// The patient's latest Persona attempt, for authorized staff only (docs/PERSONA_IDV_PLAN.md §8, §9).
// Persona's own codes (inquiry, check results, document) pass through as strings — their list belongs
// to Persona's API version; ours (the matches, the decision and its reason) are enums.
const identityAttemptSchema = z.object({
  id: z.string(),
  inquiryId: z.string().optional(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  inquiryStatus: z.string().optional(),
  documentType: z.string().optional(),
  issuingJurisdiction: z.string().optional(),
  governmentIdResult: z.string().optional(),
  selfieResult: z.string().optional(),
  nameMatch: matchOutcomeSchema.optional(),
  dobMatch: matchOutcomeSchema.optional(),
  decision: z.enum(['pass', 'review']).optional(),
  decisionReason: z.enum(DECISION_REASONS).optional(),
  policyVersion: z.string().optional(),
  /** When the sweep sent the patient to review for leaving this attempt unfinished (step 6.3). */
  abandonedAt: z.string().optional(),
  /** We expired the attempt at Persona ourselves: the patient was verified another way. */
  closedReason: z.enum(['patient_verified']).optional(),
})

// The patient's open identity follow-up task on the Care Queue (§9 target design), for the card's
// "Assigned to … · Due …" row and its link. Same link fields as Kate's check-in task.
const identityFollowUpTaskSchema = z.object({
  boardId: z.string(),
  itemId: z.string(),
  assignee: z.string().optional(),
  /** 'YYYY-MM-DD' */
  dueDate: z.string().optional(),
})

// Provider/admin-facing: the patient's identity status for the Identity Verification card
// (docs/PERSONA_IDV_PLAN.md §7). Grows with the Persona pilot (per-check outcomes, attempts); the
// `verified` flag stays the one access-relevant field.
export const getIdentityVerification = oc
  .route({ method: 'GET', path: '/v2/patient/{id}/identity-verification' })
  .input(z.object({ id: z.string() }))
  .output(
    z.object({
      id: z.string(),
      verified: z.boolean(),
      status: identityStatusSchema,
      method: identityMethodSchema.optional(),
      verifiedAt: z.string().optional(),
      verifiedBy: z.string().optional(),
      /** Absent when there is no attempt or the caller is not an identity reviewer (§8). */
      latestAttempt: identityAttemptSchema.optional(),
      /** Only while the patient is unverified: verifying closes the task. */
      followUpTask: identityFollowUpTaskSchema.optional(),
      /**
       * Whether the patient is in the Persona pilot cohort, and whether the caller (a care
       * coordinator or doctor) may switch that from the chart.
       */
      personaEnrollment: z.object({ enrolled: z.boolean(), canChange: z.boolean() }),
    }),
  )

// Provider-facing: the chart's switch for the Persona pilot — the same cohort enrolment as the Super
// **Persona pilot** page, for care coordinators and doctors (§8).
export const setPersonaEnrollment = oc
  .route({ method: 'POST', path: '/v2/patient/{id}/identity-verification/persona-enrollment' })
  .input(z.object({ id: z.string(), enrolled: z.boolean() }))
  .output(z.object({ enrolled: z.boolean() }))

// Provider-facing: the chart's **Check with Persona** — syncs the patient's open Persona attempt now
// rather than waiting for the webhook or the 5-minute sweep; the card then re-reads with the GET.
// `/persona-check`, not `/check`: `{id}/…/check` would also match the patient's own
// `/v2/patient/me/identity-verification/check` with `id = "me"` (see `orpc/contracts.spec.ts`).
// `updated`: our record changed. `unchanged`: Persona has nothing new yet. `no_open_attempt`: nothing
// is waiting at Persona. `unavailable`: Persona is not configured here or could not be reached.
export const checkIdentityVerification = oc
  .route({ method: 'POST', path: '/v2/patient/{id}/identity-verification/persona-check' })
  .input(z.object({ id: z.string() }))
  .output(z.object({ outcome: z.enum(['updated', 'unchanged', 'no_open_attempt', 'unavailable']) }))

// Provider-facing: the front of the ID Persona checked for the patient's latest attempt, as the
// image file. Fetched live from Persona on every call and never stored — the photos stay with Persona
// (§8). Identity reviewers only. NOT_FOUND when Persona has none (never reached, or redacted). A POST,
// like the combined PDF, so no HTTP cache keeps the image.
export const getPersonaIdImage = oc
  .route({ method: 'POST', path: '/v2/patient/{id}/identity-verification/persona-id-image' })
  .input(z.object({ id: z.string() }))
  .output(z.file())
